import { Injectable, inject } from '@angular/core';
import { INGXLoggerServerService, INGXLoggerMetadata, INGXLoggerConfig } from 'ngx-logger';
import { ErrorProcessor } from './error-processor';
import { ERROR_HANDLER_CONFIG } from './error-handler.service';

/**
 * Servicio personalizado que extiende el comportamiento del servidor de NGXLogger.
 * Decorar automáticamente todos los logs enviados al servidor con contexto y error procesado.
 */
@Injectable({ providedIn: 'root' })
export class CustomNGXLoggerServerService implements INGXLoggerServerService {
  private errorProcessorConfig = inject(ERROR_HANDLER_CONFIG, { optional: true });
  private errorProcessor: ErrorProcessor;

  constructor() {
    // Inicializar ErrorProcessor con la configuración inyectada
    this.errorProcessor = new ErrorProcessor(this.errorProcessorConfig || {});
  }

  /**
   * Método llamado por NGXLogger antes de enviar cada log al servidor.
   * Enriquecer el metadata con contexto y procesamiento de errores.
   */
  public sendToServer(metadata: INGXLoggerMetadata, config: INGXLoggerConfig): void {
    try {
      const level = this.sanitizeLevel(metadata.level);

      if (!level) {
        return;
      }

      // Capturar contexto actual (sessionId, url, viewport, etc.)
      const context = this.errorProcessor.captureContext();

      // Analizar si hay errores en los additional parameters
      const allAdditional = [];

      if (
        typeof metadata.message === 'string' &&
        this.errorProcessor.shouldIgnoreError(metadata.message)
      ) {
        return;
      }

      if (metadata.message) {
        allAdditional.push(metadata.message);
      }
      if (Array.isArray(metadata.additional) && metadata.additional.length > 0) {
        allAdditional.push(...metadata.additional);
      }
      if (allAdditional.length === 0) {
        return;
      }

      // Buscar si alguno de los additional es un Error
      let processedError = null;
      let hasError = false;

      for (const item of allAdditional) {
        if (
          item instanceof Error ||
          (typeof item === 'object' && item && 'name' in item && 'message' in item)
        ) {
          hasError = true;
          processedError = this.errorProcessor.extractErrorInfo(item);
          break;
        }
      }

      // Extraer customMetadata de la configuración
      const customMetadata = this.errorProcessorConfig?.customMetadata;

      // Decorar metadata con contexto y error procesado
      const enrichedMetadata: INGXLoggerMetadata = {
        ...metadata,
        level,
        additional: [
          {
            context,
            ...(processedError && { processedError }),
            ...(customMetadata && { customMetadata }),
            data: allAdditional, // Array con todos los inputs originales
          },
        ],
      };

      // Enviar al servidor usando el comportamiento original (fetch)
      const originalServerLoggingUrl = config.serverLoggingUrl;
      if (originalServerLoggingUrl) {
        fetch(originalServerLoggingUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: this.errorProcessor.safeStringify(enrichedMetadata),
        }).catch(error => {
          // Loggear error de envío silenciosamente
          console.error('[CustomNGXLoggerServerService] Failed to send log to server:', error);
        });
      }
    } catch (error) {
      console.error('[CustomNGXLoggerServerService] Failed to enrich log:', error);

      // Fallback: enviar log original sin enriquecer
      const originalServerLoggingUrl = config.serverLoggingUrl;
      if (originalServerLoggingUrl) {
        fetch(originalServerLoggingUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: this.errorProcessor.safeStringify(metadata),
        }).catch(err => {
          console.error('[CustomNGXLoggerServerService] Failed to send original log:', err);
        });
      }
    }
  }

  private sanitizeLevel(level: any): number | undefined {
    if (!level) {
      return undefined;
    }
    // Si es un número válido entre 0-6, retornarlo
    if (typeof level === 'number' && level >= 0 && level <= 6) {
      return Math.floor(level); // Asegurar que sea entero
    }

    // Si es un string que representa un número válido, convertirlo
    if (typeof level === 'string') {
      const parsed = parseInt(level, 10);
      if (!isNaN(parsed) && parsed >= 0 && parsed <= 6) {
        return parsed;
      }
    }
    return undefined;
  }

  /**
   * Flush the queue of the logger (requerido por la interfaz)
   */
  public flushQueue(config: INGXLoggerConfig): void {
    // No implementamos cola en este custom service, no hacer nada
  }
}
